var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useUXPContext } from '@core/context/useUXPContext';
import MediaThumb from './MediaThumb';
import { mediaValueName, mediaValueType } from './mediaLibrary';
import { getUrlExtension } from '../FileInput/fileHelper';
/** Text formats plain enough to show as-is. */
const TEXT_EXTENSIONS = ['csv', 'json', 'txt', 'md', 'log'];
/**
 * Most of a text file a preview will read. An exported CSV can be hundreds of
 * megabytes and the preview only ever shows its first screenful, so the
 * transfer is stopped here rather than pulled into memory whole.
 */
const MAX_TEXT_BYTES = 100 * 1024;
/** Classification is the library's, not this component's — see mediaValueType. */
function previewKind(value) {
    const type = mediaValueType(value);
    if (type === 'Image' || type === 'Icon')
        return 'thumb';
    if (type === 'Video')
        return 'video';
    if (type === 'Document') {
        const ext = getUrlExtension(value);
        if (ext === 'pdf')
            return 'pdf';
        if (TEXT_EXTENSIONS.indexOf(ext) >= 0)
            return 'text';
    }
    return 'unsupported';
}
/**
 * A file's text, capped at MAX_TEXT_BYTES. Rejects on anything that means there
 * is nothing to show — a network or CORS failure, or a non-ok status.
 */
function fetchCappedText(url) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield fetch(url);
        if (!response.ok)
            throw new Error(`Media preview request failed with ${response.status}`);
        const stream = response.body;
        const reader = stream && typeof stream.getReader === 'function' ? stream.getReader() : null;
        if (!reader) {
            // No stream to stop early — read it whole and cut it down. Only reached
            // where `body` isn't exposed; the cap on what is rendered still holds.
            const whole = yield response.text();
            return { content: whole.slice(0, MAX_TEXT_BYTES), truncated: whole.length > MAX_TEXT_BYTES };
        }
        const chunks = [];
        let size = 0;
        let finished = false;
        while (size < MAX_TEXT_BYTES) {
            const { done, value } = yield reader.read();
            if (done) {
                finished = true;
                break;
            }
            chunks.push(value);
            size += value.length;
        }
        // The point of the cap is not to download the rest of the file.
        if (!finished)
            reader.cancel().catch(() => { });
        const capped = new Uint8Array(Math.min(size, MAX_TEXT_BYTES));
        let offset = 0;
        for (const chunk of chunks) {
            if (offset >= capped.length)
                break;
            const part = chunk.subarray(0, capped.length - offset);
            capped.set(part, offset);
            offset += part.length;
        }
        return { content: new TextDecoder().decode(capped), truncated: !finished };
    });
}
/**
 * @export
 * The large, single preview of a media value: a picture or icon through
 * MediaThumb, a video's first frame, a PDF in a frame, a small text file as
 * text, and a type glyph with "preview not available" for everything else.
 *
 * Deliberately separate from MediaThumb, which draws every tile of a grid: a
 * `<video>`, an `<iframe>` or a `fetch` in that component would mean one per
 * tile. This one is rendered once, by MediaPreviewPanel, and hands the cases
 * MediaThumb already covers straight back to it.
 */
const MediaPreview = (props) => {
    const { value, name } = props;
    const context = useUXPContext();
    const kind = previewKind(value);
    const [videoFailed, setVideoFailed] = React.useState(false);
    const [text, setText] = React.useState(null);
    const [failed, setFailed] = React.useState(false);
    // Only the newest request may write state — the guard LibraryView.load()
    // uses, here so a slow file can't paint over a newer selection.
    const requestId = React.useRef(0);
    // A new value deserves a fresh attempt at the video.
    React.useEffect(() => setVideoFailed(false), [value]);
    React.useEffect(() => {
        // Bumped for every value, not just text ones: it is what invalidates a
        // fetch still in flight for the value being replaced.
        const id = ++requestId.current;
        setText(null);
        setFailed(false);
        if (kind !== 'text')
            return;
        fetchCappedText(value)
            .then(result => {
            if (id !== requestId.current)
                return;
            setText(result);
        })
            .catch(() => {
            if (id !== requestId.current)
                return;
            // CORS, 404, a non-ok status, a stream that died — every one of
            // them lands on the same "no preview" state.
            setFailed(true);
        });
    }, [value, kind]);
    /** Type glyph plus a plain statement that this one can't be shown. */
    function renderUnavailable() {
        return _jsxs("div", { className: "uxpcore_mediapicker__preview-fallback", children: [_jsx(MediaThumb, { value: value, name: name }), _jsx("div", { className: "uxpcore_mediapicker__message", children: context.$L('uxp-core.media-picker.preview-unavailable') })] });
    }
    if (kind === 'thumb')
        return _jsx(MediaThumb, { value: value, name: name, fit: "contain" });
    if (kind === 'video' && !videoFailed) {
        // First frame only: `preload="metadata"` is enough for the browser to
        // paint one, and there are deliberately no controls and no autoplay.
        return _jsx("video", { className: "uxpcore_mediapicker__preview-media", src: value, preload: "metadata", muted: true, playsInline: true, onError: () => setVideoFailed(true) });
    }
    if (kind === 'pdf') {
        // A cross-origin PDF can refuse framing (X-Frame-Options / CSP) and the
        // browser fires no event a page can read for that, so a blocked file
        // shows the browser's own empty frame. That is not a bug here: telling
        // it apart would take polling or a timer, and neither is reliable.
        return _jsx("iframe", { className: "uxpcore_mediapicker__preview-media uxpcore_mediapicker__preview-media--fill", src: value, title: name || mediaValueName(value) });
    }
    if (kind === 'text') {
        // Pending is the absence of both outcomes, which also covers the render
        // before the effect has run — deriving it rather than holding a third
        // state is what keeps "not available" from flashing up first.
        if (!text && !failed) {
            return _jsx("div", { className: "uxpcore_mediapicker__message", children: context.$L('uxp-core.media-picker.preview-loading') });
        }
        if (text) {
            return _jsxs("div", { className: "uxpcore_mediapicker__preview-media uxpcore_mediapicker__preview-media--fill uxpcore_mediapicker__preview-textbox", children: [_jsx("pre", { className: "uxpcore_mediapicker__preview-text", children: text.content }), text.truncated &&
                        _jsx("div", { className: "uxpcore_mediapicker__preview-truncated", children: context.$L('uxp-core.media-picker.preview-truncated') })] });
        }
    }
    return renderUnavailable();
};
export default MediaPreview;
